import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default function Nomina() {
  const [periodo, setPeriodo] = useState("2024-11");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);

  const [nominaData, setNominaData] = useState([]);

  useEffect(() => {
    const fetchNomina = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.RRHH.NOMINA, {
          params: { periodo },
        });
        setNominaData(response.data);
      } catch (error) {
        console.error("Error al cargar nómina:", error);
      }
    };
    fetchNomina();
  }, [periodo]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const handleVerDetalle = (empleado) => {
    setSelectedEmpleado(empleado);
    setOpenDialog(true);
  };

  const totalPercepciones = nominaData.reduce((sum, emp) => sum + emp.salarioBruto, 0);
  const totalDeducciones = nominaData.reduce(
    // eslint-disable-next-line
    (sum, emp) => sum + Object.values(emp.deducciones || {}).reduce((a, b) => a + b, 0),
    0
  );
  const totalNeto = nominaData.reduce((sum, emp) => sum + emp.salarioNeto, 0);

  return (
    <MDBox>
      <Grid container spacing={3}>
        {/* Resumen de nómina */}
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="text">
                Total Percepciones
              </MDTypography>
              <MDTypography variant="h3" color="success" fontWeight="bold">
                {formatCurrency(totalPercepciones)}
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="text">
                Total Deducciones
              </MDTypography>
              <MDTypography variant="h3" color="error" fontWeight="bold">
                {formatCurrency(totalDeducciones)}
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="text">
                Neto a Pagar
              </MDTypography>
              <MDTypography variant="h3" color="info" fontWeight="bold">
                {formatCurrency(totalNeto)}
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Tabla de nómina */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Nómina del Período
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <TextField
                    type="month"
                    label="Período"
                    value={periodo}
                    onChange={(e) => setPeriodo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <MDButton variant="gradient" color="success">
                    <Icon>file_download</Icon>
                    &nbsp;Exportar
                  </MDButton>
                  <MDButton variant="gradient" color="info">
                    <Icon>calculate</Icon>
                    &nbsp;Calcular Nómina
                  </MDButton>
                </MDBox>
              </MDBox>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>No. Empleado</TableCell>
                      <TableCell>Empleado</TableCell>
                      <TableCell align="right">Salario Bruto</TableCell>
                      <TableCell align="right">Percepciones</TableCell>
                      <TableCell align="right">Deducciones</TableCell>
                      <TableCell align="right">Salario Neto</TableCell>
                      <TableCell align="center">Estatus</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nominaData.map((row) => {
                      // eslint-disable-next-line
                      const totalPerc = Object.values(row.percepciones || {}).reduce((a, b) => a + b, 0);
                      // eslint-disable-next-line
                      const totalDed = Object.values(row.deducciones || {}).reduce((a, b) => a + b, 0);
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <MDTypography variant="caption" fontWeight="medium">
                              {row.numEmpleado}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant="caption">{row.empleado}</MDTypography>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="caption" fontWeight="medium">
                              {formatCurrency(row.salarioBruto)}
                            </MDTypography>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="caption" color="success">
                              {formatCurrency(totalPerc)}
                            </MDTypography>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="caption" color="error">
                              {formatCurrency(totalDed)}
                            </MDTypography>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="caption" fontWeight="bold" color="info">
                              {formatCurrency(row.salarioNeto)}
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={row.estatus}
                              color={row.estatus === "Pagado" ? "success" : "warning"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <MDButton
                              variant="text"
                              color="info"
                              size="small"
                              onClick={() => handleVerDetalle(row)}
                            >
                              <Icon>visibility</Icon>
                            </MDButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de detalle */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recibo de Nómina - {selectedEmpleado?.empleado}</DialogTitle>
        <DialogContent>
          {selectedEmpleado && (
            <MDBox mt={2}>
              <MDTypography variant="caption" fontWeight="bold" mb={1}>
                No. Empleado: {selectedEmpleado.numEmpleado}
              </MDTypography>
              <MDTypography variant="caption" display="block" mb={2}>
                Período: {periodo}
              </MDTypography>

              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold" color="success" mb={1}>
                  Percepciones
                </MDTypography>
                {Object.entries(selectedEmpleado.percepciones || {}).map(([key, value]) => (
                  <MDBox key={key} display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="caption">{key}</MDTypography>
                    <MDTypography variant="caption" fontWeight="medium">
                      {formatCurrency(value)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>

              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold" color="error" mb={1}>
                  Deducciones
                </MDTypography>
                {Object.entries(selectedEmpleado.deducciones || {}).map(([key, value]) => (
                  <MDBox key={key} display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="caption">{key}</MDTypography>
                    <MDTypography variant="caption" fontWeight="medium">
                      {formatCurrency(value)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>

              <MDBox borderTop="1px solid #ddd" pt={2}>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="h6" fontWeight="bold">
                    Neto a Pagar
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" color="info">
                    {formatCurrency(selectedEmpleado.salarioNeto)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDialog(false)} color="secondary">
            Cerrar
          </MDButton>
          <MDButton color="error">
            <Icon>picture_as_pdf</Icon>
            &nbsp;Descargar PDF
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
